import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { createHeaders } from '@/lib/operator-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: Record<string, unknown>;
  isLoading?: boolean;
}

function AISearchPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { operator, selectedBusinessId } = useOperator();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Welcome message on first load
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          content: t('aiSearch.welcome'),
          timestamp: new Date(),
        },
      ]);
    }
  }, [t, messages.length]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const suggestions = [
    t('aiSearch.suggestion1'),
    t('aiSearch.suggestion2'),
    t('aiSearch.suggestion3'),
    t('aiSearch.suggestion4'),
    t('aiSearch.suggestion5'),
    t('aiSearch.suggestion6'),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Add loading message
    const loadingId = `loading-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: loadingId,
        role: 'assistant',
        content: t('aiSearch.thinking'),
        timestamp: new Date(),
        isLoading: true,
      },
    ]);

    try {
      const response = await fetch('/api/operator/ai-search', {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({
          query: userMessage.content,
          businessId: selectedBusinessId,
          locale: locale,
        }),
      });

      const data = await response.json();

      // Remove loading message and add response
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== loadingId);
        return [
          ...filtered,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: data.response || data.error || t('aiSearch.noResults'),
            timestamp: new Date(),
            data: data.data,
          },
        ];
      });
    } catch {
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== loadingId);
        return [
          ...filtered,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: t('aiSearch.error'),
            timestamp: new Date(),
          },
        ];
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const formatData = (data: Record<string, unknown>) => {
    if (!data) return null;

    // Format different data types for display
    if (data.type === 'sales_stats') {
      const stats = data as Record<string, unknown>;
      return (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="bg-green-50 p-2 rounded">
            <div className="text-green-800 font-medium">Revenue</div>
            <div className="text-green-600">{String(stats.totalRevenue || 'R$ 0')}</div>
          </div>
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-blue-800 font-medium">Orders</div>
            <div className="text-blue-600">{String(stats.orderCount || 0)}</div>
          </div>
        </div>
      );
    }

    if (data.type === 'bookings_list' && Array.isArray(data.bookings)) {
      return (
        <div className="mt-3 space-y-2">
          {data.bookings.slice(0, 5).map((booking: Record<string, unknown>, idx: number) => (
            <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
              <div className="font-medium">{String(booking.service || 'Service')}</div>
              <div className="text-gray-600">
                {String(booking.customerName || 'Customer')} - {String(booking.time || '')}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (data.type === 'customer_info') {
      const customer = data as Record<string, unknown>;
      return (
        <div className="mt-3 bg-gray-50 p-3 rounded text-sm">
          <div className="font-medium">{String(customer.name || 'Customer')}</div>
          <div className="text-gray-600">{String(customer.phone || '')}</div>
          <div className="text-gray-600">{String(customer.email || '')}</div>
          <div className="mt-2 flex gap-2">
            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
              {String(customer.bookingCount || 0)} bookings
            </span>
            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
              {String(customer.totalSpent || 'R$ 0')} spent
            </span>
          </div>
        </div>
      );
    }

    if (data.type === 'availability' && Array.isArray(data.slots)) {
      return (
        <div className="mt-3 flex flex-wrap gap-2">
          {data.slots.slice(0, 8).map((slot: { displayTime?: string } | string, idx: number) => (
            <span key={idx} className="bg-green-50 text-green-800 px-2 py-1 rounded text-sm">
              {typeof slot === 'string' ? slot : slot.displayTime || 'Available'}
            </span>
          ))}
        </div>
      );
    }

    return null;
  };

  if (!operator) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            Loading...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 h-[calc(100vh-80px)] flex flex-col">
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="border-b flex-shrink-0">
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            {t('aiSearch.title')}
          </CardTitle>
          <p className="text-gray-500 text-sm">{t('aiSearch.subtitle')}</p>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Messages */}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800'
                } ${message.isLoading ? 'animate-pulse' : ''}`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                {message.data && formatData(message.data)}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />

          {/* Suggestions (only show if few messages) */}
          {messages.length <= 2 && (
            <div className="pt-4">
              <p className="text-gray-500 text-sm mb-3">{t('aiSearch.suggestions')}</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="px-3 py-2 bg-gray-50 hover:bg-gray-100 border rounded-lg text-sm text-gray-700 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>

        {/* Input */}
        <div className="border-t p-4 flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('aiSearch.placeholder')}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <Button type="submit" disabled={isLoading || !input.trim()}>
              {isLoading ? t('aiSearch.thinking') : t('aiSearch.send')}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/ai-search')({
  component: AISearchPage,
});
